import { Controller } from "@hotwired/stimulus"

// Voice console for Oscar Receptionist.
// Uses Web Speech API for speech-to-text and ElevenLabs TTS for voice output.
export default class extends Controller {
  static targets = [
    "status", "waveform", "micBtn", "micLabel", "transcript",
    "credits", "resetBtn", "modeRadio", "contactPicker", "anonFields",
    "debugContainer"
  ]
  static values = { sendUrl: String, ttsUrl: String, startUrl: String, csrf: String, greeting: String }

  connect() {
    this.conversation = []
    this.listening = false
    this.speaking = false
    this.processing = false
    this.silenceTimer = null
    this.finalTranscript = ""
    this.audioUnlocked = false
    this.recognition = null
    this.initSpeechRecognition()
  }

  disconnect() {
    this.stopListening()
  }

  initSpeechRecognition() {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition
    if (!SpeechRecognition) {
      this.setStatus("Speech recognition not supported in this browser")
      return
    }

    this.recognition = new SpeechRecognition()
    this.recognition.continuous = true
    this.recognition.interimResults = true
    this.recognition.lang = "en-US"

    this.recognition.onresult = (event) => {
      let finalText = ""
      let interim = ""

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i]
        if (result.isFinal) {
          finalText += result[0].transcript
        } else {
          interim += result[0].transcript
        }
      }

      if (finalText) this.finalTranscript += finalText
      const display = this.finalTranscript || interim
      if (display.trim()) this.setStatus(`"${display.trim()}"`)

      // Reset silence timer on new speech
      clearTimeout(this.silenceTimer)
      if (this.finalTranscript.trim().length >= 2) {
        this.silenceTimer = setTimeout(() => this.submitSpeech(), 1500)
      }
    }

    this.recognition.onerror = (event) => {
      if (event.error === "aborted" || event.error === "no-speech") return
      if (event.error === "not-allowed") {
        this.setStatus("Microphone access denied")
        return
      }
      // Auto-restart on transient errors
      setTimeout(() => {
        if (this.listening && !this.speaking) this.startListening()
      }, 500)
    }

    this.recognition.onend = () => {
      // Auto-restart if we should still be listening
      if (this.listening && !this.speaking && !this.processing) {
        try { this.recognition.start() } catch (e) { /* already started */ }
      }
    }
  }

  async toggleMic() {
    this.unlockAudio()

    if (this.listening) {
      this.stopListening()
      this.setStatus("Click the microphone to start")
      this.updateMicUI()
      return
    }

    // Request mic permission explicitly before starting speech recognition
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      // Stop the stream immediately — we just needed the permission
      stream.getTracks().forEach(t => t.stop())
    } catch (e) {
      this.setStatus("Microphone access denied. Please allow microphone access in your browser settings.")
      return
    }

    if (this.conversation.length === 0) {
      const greeting = await this.resolveGreeting()
      if (greeting) {
        this.playGreeting(greeting)
      } else {
        this.startListening()
      }
    } else {
      this.startListening()
    }
  }

  async resolveGreeting() {
    if (!this.hasStartUrlValue) return this.greetingValue

    try {
      const response = await fetch(this.startUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfValue
        },
        body: JSON.stringify({
          contact_id: this.getContactId(),
          test_phone: this.getTestPhone()
        })
      })

      if (response.ok) {
        const data = await response.json()
        return data.greeting || this.greetingValue
      }
    } catch (e) { /* fall through to static greeting */ }

    return this.greetingValue
  }

  async playGreeting(greeting) {
    this.setStatus("Oscar is greeting...")
    this.setSpeaking(true)

    this.appendTranscript("Oscar", greeting)
    this.conversation.push({ role: "assistant", content: greeting })
    this.showReset()

    await this.playTts(greeting)

    this.setSpeaking(false)
    this.startListening()
  }

  startListening() {
    if (!this.recognition) return
    this.listening = true
    this.finalTranscript = ""
    this.setStatus("Listening...")
    this.updateMicUI()
    try { this.recognition.start() } catch (e) { /* already started */ }
  }

  stopListening() {
    this.listening = false
    clearTimeout(this.silenceTimer)
    if (this.recognition) {
      try { this.recognition.stop() } catch (e) { /* already stopped */ }
    }
    this.updateMicUI()
  }

  async submitSpeech() {
    const text = this.finalTranscript.trim()
    if (text.length < 2) return

    this.stopListening()
    this.processing = true
    this.setStatus("Processing...")
    this.updateMicUI()
    this.showReset()

    this.appendTranscript("You", text)
    this.conversation.push({ role: "user", content: text })

    try {
      const response = await fetch(this.sendUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfValue
        },
        body: JSON.stringify({
          messages: this.conversation,
          contact_type: this.getMode() === "prospect" ? "prospect" : null,
          contact_id: this.getContactId(),
          test_phone: this.getTestPhone()
        })
      })

      if (response.ok) {
        const data = await response.json()
        this.conversation.push({ role: "assistant", content: data.content })
        this.appendTranscript("Oscar", data.content)
        if (data.debug) this.appendDebug(data.debug)
        if (Array.isArray(data.back_stops) && data.back_stops.length > 0) this.appendBackStops(data.back_stops)
        this.updateCredits(data.credits_consumed)

        // Speak the response
        this.processing = false
        this.setSpeaking(true)
        this.setStatus("Oscar is speaking...")
        await this.playTts(data.content)
        this.setSpeaking(false)

        // Resume listening
        this.startListening()
      } else {
        const data = await response.json().catch(() => ({}))
        this.appendTranscript("Error", data.error || "Something went wrong")
        this.processing = false
        this.startListening()
      }
    } catch (err) {
      this.appendTranscript("Error", "Network error")
      this.processing = false
      this.startListening()
    }
  }

  async playTts(text) {
    try {
      const response = await fetch(this.ttsUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfValue
        },
        body: JSON.stringify({ text })
      })

      if (!response.ok) {
        // Fallback to browser speech synthesis
        return this.fallbackSpeak(text)
      }

      const blob = await response.blob()
      const url = URL.createObjectURL(blob)
      return new Promise((resolve) => {
        const audio = new Audio(url)
        audio.playbackRate = 1.1
        audio.onended = () => { URL.revokeObjectURL(url); resolve() }
        audio.onerror = () => { URL.revokeObjectURL(url); this.fallbackSpeak(text).then(resolve) }
        audio.play().catch(() => this.fallbackSpeak(text).then(resolve))
      })
    } catch (e) {
      return this.fallbackSpeak(text)
    }
  }

  fallbackSpeak(text) {
    return new Promise((resolve) => {
      if (!window.speechSynthesis) { resolve(); return }
      const utterance = new SpeechSynthesisUtterance(text)
      utterance.rate = 1.1
      utterance.onend = resolve
      utterance.onerror = resolve
      window.speechSynthesis.speak(utterance)
    })
  }

  // Mode toggle (called by SelectableCardGroupComponent)
  toggle() {
    const mode = this.getMode()
    if (this.hasContactPickerTarget) {
      this.contactPickerTarget.classList.toggle("hidden", mode !== "contact")
    }
    if (this.hasAnonFieldsTarget) {
      this.anonFieldsTarget.classList.toggle("hidden", mode !== "prospect")
    }
    this.reset()
  }

  reset() {
    this.stopListening()
    this.conversation = []
    this.processing = false
    this.setSpeaking(false)
    this.setStatus("Click the microphone to start")
    this.updateMicUI()
    if (this.hasTranscriptTarget) {
      this.transcriptTarget.innerHTML = '<p class="text-xs text-gray-400 dark:text-gray-500 italic">Conversation transcript will appear here.</p>'
    }
    if (this.hasResetBtnTarget) this.resetBtnTarget.classList.add("hidden")
    if (this.hasDebugContainerTarget) this.debugContainerTarget.innerHTML = ""
  }

  // Helpers
  getMode() {
    const checked = this.modeRadioTargets.find(r => r.checked)
    return checked ? checked.value : "prospect"
  }

  getContactId() {
    if (this.getMode() !== "contact") return null
    const input = this.element.querySelector('input[name="console[contact_id]"]')
    return input ? input.value : null
  }

  getTestPhone() {
    if (this.getMode() !== "prospect") return null
    const input = this.element.querySelector('input[name="console[test_phone]"]')
    return input ? input.value : null
  }

  setStatus(text) {
    if (this.hasStatusTarget) this.statusTarget.textContent = text
  }

  setSpeaking(speaking) {
    this.speaking = speaking
    if (!this.hasWaveformTarget) return
    const bars = this.waveformTarget.querySelectorAll("span")
    bars.forEach((bar, i) => {
      if (speaking) {
        const heights = [12, 20, 28, 32, 28, 20, 12]
        bar.style.height = `${heights[i]}px`
        bar.style.animation = `wave-bar 0.6s ease-in-out ${i * 0.08}s infinite alternate`
      } else {
        bar.style.height = "4px"
        bar.style.animation = "none"
      }
    })
  }

  updateMicUI() {
    if (!this.hasMicBtnTarget) return
    const btn = this.micBtnTarget

    if (this.speaking || this.processing) {
      btn.disabled = true
      btn.className = "inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-medium bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed"
      if (this.hasMicLabelTarget) this.micLabelTarget.textContent = this.speaking ? "Speaking..." : "Processing..."
    } else if (this.listening) {
      btn.disabled = false
      btn.className = "inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-medium bg-primary-600 text-white shadow-lg animate-pulse"
      if (this.hasMicLabelTarget) this.micLabelTarget.textContent = "Listening"
    } else {
      btn.disabled = false
      btn.className = "inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-medium bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600"
      if (this.hasMicLabelTarget) this.micLabelTarget.textContent = "Start"
    }
  }

  showReset() {
    if (this.hasResetBtnTarget) this.resetBtnTarget.classList.remove("hidden")
  }

  appendTranscript(speaker, text) {
    if (!this.hasTranscriptTarget) return
    // Clear placeholder on first message
    if (this.conversation.length <= 1 && speaker !== "Error") {
      const placeholder = this.transcriptTarget.querySelector(".italic")
      if (placeholder) placeholder.remove()
    }
    const div = document.createElement("div")
    const isOscar = speaker === "Oscar"
    div.className = "text-xs"
    div.innerHTML = `<span class="font-medium ${isOscar ? 'text-primary-600 dark:text-primary-400' : 'text-gray-700 dark:text-gray-300'}">${this.escapeHtml(speaker)}:</span> <span class="text-gray-600 dark:text-gray-400">${this.escapeHtml(text)}</span>`
    this.transcriptTarget.appendChild(div)
    this.transcriptTarget.scrollTop = this.transcriptTarget.scrollHeight
  }

  updateCredits(consumed) {
    if (!this.hasCreditsTarget) return
    const current = this.creditsTarget.textContent.trim()
    if (current === "∞") return
    const remaining = Math.max(0, parseInt(current) - consumed)
    this.creditsTarget.textContent = remaining
  }

  appendBackStops(backStops) {
    // Surfaces hallucination back-stops in the test console so staff can
    // see what would happen in production. In production these proposals
    // get persisted as Tasks; in the test console we just display them.
    if (!this.hasTranscriptTarget) return
    const div = document.createElement("div")
    div.className = "rounded-md border border-amber-300 bg-amber-50 px-2 py-1.5 text-[10px] text-amber-800 mt-1"
    const items = backStops.map(b =>
      `<div>⚠️ <span class="font-medium uppercase">${this.escapeHtml(b.kind)}</span> — ${this.escapeHtml(b.display_label || '')}. In production: would auto-create a follow-up task.</div>`
    ).join("")
    div.innerHTML = items
    this.transcriptTarget.appendChild(div)
    this.transcriptTarget.scrollTop = this.transcriptTarget.scrollHeight
  }

  appendDebug(debug) {
    if (!this.hasDebugContainerTarget) return
    let toolSection = ""
    if (debug.tool_calls && debug.tool_calls.length > 0) {
      const toolLines = debug.tool_calls.map(tc => {
        const args = JSON.stringify(tc.arguments, null, 2)
        let result = ""
        try { result = JSON.stringify(JSON.parse(tc.result), null, 2) } catch { result = tc.result }
        return `<div class="mb-2"><span class="font-medium text-amber-400">${this.escapeHtml(tc.tool)}</span>(${this.escapeHtml(args)})\n<span class="text-gray-500">→</span> ${this.escapeHtml(result)}</div>`
      }).join("")
      toolSection = `<div class="mt-2 border-t border-gray-700 pt-2"><div class="text-[10px] font-medium text-gray-400 mb-1">Tool Calls</div>${toolLines}</div>`
    }

    this.debugContainerTarget.innerHTML = `
      <details class="group">
        <summary class="text-[10px] text-gray-500 cursor-pointer hover:text-gray-400 select-none">
          Debug · ${debug.model || "unknown"} · ${debug.input_tokens || 0} in / ${debug.output_tokens || 0} out
        </summary>
        <div class="mt-1 rounded bg-gray-900 border border-gray-700 p-3 text-[11px] font-mono text-gray-300 max-h-64 overflow-auto whitespace-pre-wrap">
          <div class="text-[10px] font-medium text-gray-400 mb-1">System Prompt</div>
          <div class="text-gray-400">${this.escapeHtml(debug.system_prompt || "")}</div>
          ${toolSection}
        </div>
      </details>
    `
  }

  unlockAudio() {
    if (this.audioUnlocked) return
    this.audioUnlocked = true
    const silentWav = "data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQAAAAA="
    const a = new Audio(silentWav)
    a.volume = 0.01
    a.play().then(() => a.pause()).catch(() => {})
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
